import { patientCommunicationPath } from '@/lib/patientCommunication';

function formatMoney(cents: number, currency: string = 'DKK'): string {
  return (cents / 100).toLocaleString('da-DK', {
    style: 'currency',
    currency,
  });
}

export const ACTIVITY_CATEGORIES = [
  'all',
  'appointments',
  'journal',
  'invoices',
  'payments',
  'communications',
  'klippekort',
] as const;

export type ActivityCategory = (typeof ACTIVITY_CATEGORIES)[number];

export const ACTIVITY_DATE_PRESETS = [
  'all',
  'today',
  'this_week',
  'this_month',
  'last_3_months',
  'custom',
] as const;

export type ActivityDatePreset = (typeof ACTIVITY_DATE_PRESETS)[number];

export const ACTIVITY_EVENT_TYPES = [
  'appointment_booked',
  'appointment_rescheduled',
  'appointment_cancelled',
  'appointment_completed',
  'appointment_no_show',
  'journal_created',
  'journal_finalized',
  'journal_amendment_finalized',
  'invoice_created',
  'invoice_sent',
  'invoice_rykker',
  'invoice_paid',
  'invoice_voided',
  'payment_received',
  'payment_refunded',
  'email_sent',
  'sms_sent',
  'questionnaire_sent',
  'appointment_reminder_sent',
  'punch_card_purchased',
  'punch_card_redeemed',
  'punch_card_returned',
] as const;

export type ActivityEventType = (typeof ACTIVITY_EVENT_TYPES)[number];

export type ActivityCategoryFilter = Exclude<ActivityCategory, 'all'>;

export interface PatientActivityPayload {
  service_name?: string | null;
  practitioner_name?: string | null;
  journal_title?: string | null;
  invoice_number?: string | null;
  amount_cents?: number | null;
  currency?: string | null;
  payment_method?: string | null;
  reminder_number?: number | null;
  from_starts_at?: string | null;
  to_starts_at?: string | null;
  punch_card_name?: string | null;
  clips?: number | null;
  email_subject?: string | null;
  questionnaire_name?: string | null;
}

export interface PatientActivityItem {
  id: string;
  occurred_at: string;
  category: ActivityCategoryFilter;
  event_type: ActivityEventType;
  clinic_id: string;
  appointment_id: string | null;
  journal_entry_id: string | null;
  invoice_id: string | null;
  payment_id: string | null;
  email_id: string | null;
  sms_id: string | null;
  questionnaire_id: string | null;
  punch_card_id: string | null;
  payload: PatientActivityPayload;
}

export interface ActivityFilterState {
  category: ActivityCategory;
  datePreset: ActivityDatePreset;
  customFrom: string;
  customTo: string;
}

export const EMPTY_ACTIVITY_FILTERS: ActivityFilterState = {
  category: 'all',
  datePreset: 'all',
  customFrom: '',
  customTo: '',
};

export type ActivityFilterChipKey = 'category' | 'date';

export type ActivityTranslate = (key: string, options?: Record<string, unknown>) => string;

const EVENT_TITLE_KEYS: Record<ActivityEventType, string> = {
  appointment_booked: 'patients.activity.event.appointment_booked',
  appointment_rescheduled: 'patients.activity.event.appointment_rescheduled',
  appointment_cancelled: 'patients.activity.event.appointment_cancelled',
  appointment_completed: 'patients.activity.event.appointment_completed',
  appointment_no_show: 'patients.activity.event.appointment_no_show',
  journal_created: 'patients.activity.event.journal_created',
  journal_finalized: 'patients.activity.event.journal_finalized',
  journal_amendment_finalized: 'patients.activity.event.journal_amendment_finalized',
  invoice_created: 'patients.activity.event.invoice_created',
  invoice_sent: 'patients.activity.event.invoice_sent',
  invoice_rykker: 'patients.activity.event.invoice_rykker',
  invoice_paid: 'patients.activity.event.invoice_paid',
  invoice_voided: 'patients.activity.event.invoice_voided',
  payment_received: 'patients.activity.event.payment_received',
  payment_refunded: 'patients.activity.event.payment_refunded',
  email_sent: 'patients.activity.event.email_sent',
  sms_sent: 'patients.activity.event.sms_sent',
  questionnaire_sent: 'patients.activity.event.questionnaire_sent',
  appointment_reminder_sent: 'patients.activity.event.appointment_reminder_sent',
  punch_card_purchased: 'patients.activity.event.punch_card_purchased',
  punch_card_redeemed: 'patients.activity.event.punch_card_redeemed',
  punch_card_returned: 'patients.activity.event.punch_card_returned',
};

const EVENT_TITLE_FALLBACK: Record<ActivityEventType, string> = {
  appointment_booked: 'Appointment booked',
  appointment_rescheduled: 'Appointment rescheduled',
  appointment_cancelled: 'Appointment cancelled',
  appointment_completed: 'Appointment completed',
  appointment_no_show: 'No-show',
  journal_created: 'Journal created',
  journal_finalized: 'Journal finalized',
  journal_amendment_finalized: 'Correction finalized',
  invoice_created: 'Invoice created',
  invoice_sent: 'Invoice sent',
  invoice_rykker: 'Rykker {{number}} sent',
  invoice_paid: 'Invoice paid',
  invoice_voided: 'Invoice cancelled',
  payment_received: 'Payment received',
  payment_refunded: 'Payment refunded',
  email_sent: 'Email sent',
  sms_sent: 'SMS sent',
  questionnaire_sent: 'Questionnaire sent',
  appointment_reminder_sent: 'Appointment reminder sent',
  punch_card_purchased: 'Punch card purchased',
  punch_card_redeemed: 'Clip redeemed',
  punch_card_returned: 'Clip returned',
};

export function isActivityEventType(value: string): value is ActivityEventType {
  return (ACTIVITY_EVENT_TYPES as readonly string[]).includes(value);
}

export function isActivityCategoryFilter(value: string): value is ActivityCategoryFilter {
  return value !== 'all' && (ACTIVITY_CATEGORIES as readonly string[]).includes(value);
}

export function activityTitle(event: PatientActivityItem, t: ActivityTranslate): string {
  const type = event.event_type;
  switch (type) {
    case 'invoice_rykker':
      return t(EVENT_TITLE_KEYS[type], {
        number: event.payload.reminder_number ?? 1,
        defaultValue: EVENT_TITLE_FALLBACK[type],
      });
    case 'appointment_booked':
    case 'appointment_rescheduled':
    case 'appointment_cancelled':
    case 'appointment_completed':
    case 'appointment_no_show':
    case 'journal_created':
    case 'journal_finalized':
    case 'journal_amendment_finalized':
    case 'invoice_created':
    case 'invoice_sent':
    case 'invoice_paid':
    case 'invoice_voided':
    case 'payment_received':
    case 'payment_refunded':
    case 'email_sent':
    case 'sms_sent':
    case 'questionnaire_sent':
    case 'appointment_reminder_sent':
    case 'punch_card_purchased':
    case 'punch_card_redeemed':
    case 'punch_card_returned':
      return t(EVENT_TITLE_KEYS[type], { defaultValue: EVENT_TITLE_FALLBACK[type] });
    default: {
      const _exhaustive: never = type;
      return _exhaustive;
    }
  }
}

function clockTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('da-DK', {
    timeZone: 'Europe/Copenhagen',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

function appointmentDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat('da-DK', {
    timeZone: 'Europe/Copenhagen',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((entry) => entry.type === type)?.value ?? '';
  const day = part('day');
  const month = part('month');
  const year = part('year');
  if (!day || !month || !year) return null;
  return `${day}.${month}.${year}`;
}

function moneyLabel(payload: PatientActivityPayload): string | null {
  if (payload.amount_cents == null) return null;
  return formatMoney(payload.amount_cents, payload.currency ?? 'DKK');
}

function joinDetail(parts: Array<string | null | undefined>): string {
  return parts
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean)
    .join(' · ');
}

export function activityDetail(event: PatientActivityItem, t: ActivityTranslate): string {
  const payload = event.payload;
  switch (event.event_type) {
    case 'appointment_rescheduled': {
      const from = clockTime(payload.from_starts_at);
      const to = clockTime(payload.to_starts_at);
      const moved =
        from && to
          ? t('patients.activity.detail.rescheduled', {
              from,
              to,
              defaultValue: 'Moved from {{from}} to {{to}}',
            })
          : null;
      return joinDetail([payload.service_name, payload.practitioner_name, moved]);
    }
    case 'appointment_cancelled': {
      const date = appointmentDate(payload.to_starts_at);
      const time = clockTime(payload.to_starts_at);
      const when =
        date && time
          ? t('patients.activity.detail.appointment_at', {
              date,
              time,
              defaultValue: '{{date}} kl. {{time}}',
            })
          : null;
      return joinDetail([payload.service_name, payload.practitioner_name, when]);
    }
    case 'appointment_booked':
    case 'appointment_completed':
    case 'appointment_no_show':
    case 'appointment_reminder_sent':
      return joinDetail([payload.service_name, payload.practitioner_name]);
    case 'journal_created':
    case 'journal_finalized':
    case 'journal_amendment_finalized':
      return joinDetail([payload.journal_title, payload.practitioner_name]);
    case 'invoice_created':
    case 'invoice_sent':
    case 'invoice_paid':
    case 'invoice_voided':
    case 'invoice_rykker':
      return joinDetail([
        payload.invoice_number
          ? t('patients.activity.detail.invoice', {
              number: payload.invoice_number,
              defaultValue: 'Invoice {{number}}',
            })
          : t('patients.activity.detail.invoice_draft', { defaultValue: 'Draft invoice' }),
        moneyLabel(payload),
      ]);
    case 'payment_received':
    case 'payment_refunded':
      return joinDetail([moneyLabel(payload), payload.payment_method, payload.invoice_number]);
    case 'email_sent':
      return joinDetail([payload.email_subject]);
    case 'sms_sent':
      return '';
    case 'questionnaire_sent':
      return joinDetail([payload.questionnaire_name]);
    case 'punch_card_purchased':
      return joinDetail([payload.punch_card_name, moneyLabel(payload)]);
    case 'punch_card_redeemed':
    case 'punch_card_returned':
      return joinDetail([
        payload.punch_card_name,
        payload.clips != null
          ? t('patients.activity.detail.clips', {
              count: payload.clips,
              defaultValue: '{{count}} clips',
            })
          : null,
        payload.service_name,
      ]);
    default: {
      const _exhaustive: never = event.event_type;
      return _exhaustive;
    }
  }
}

export function activityActionLabel(
  event: PatientActivityItem,
  t: ActivityTranslate,
): string | null {
  switch (event.event_type) {
    case 'appointment_booked':
    case 'appointment_rescheduled':
    case 'appointment_cancelled':
    case 'appointment_completed':
    case 'appointment_no_show':
    case 'appointment_reminder_sent':
      return event.appointment_id
        ? t('patients.activity.action.view_appointment', { defaultValue: 'View appointment' })
        : null;
    case 'journal_created':
    case 'journal_finalized':
    case 'journal_amendment_finalized':
      return event.journal_entry_id
        ? t('patients.activity.action.view_journal', { defaultValue: 'View journal' })
        : null;
    case 'invoice_created':
    case 'invoice_sent':
    case 'invoice_rykker':
    case 'invoice_paid':
    case 'invoice_voided':
      return event.invoice_id
        ? t('patients.activity.action.view_invoice', { defaultValue: 'View invoice' })
        : null;
    case 'payment_received':
    case 'payment_refunded':
      return event.invoice_id
        ? t('patients.activity.action.view_payment', { defaultValue: 'View payment' })
        : null;
    case 'email_sent':
      return t('patients.activity.action.view_email', { defaultValue: 'View email' });
    case 'sms_sent':
      return t('patients.activity.action.view_sms', { defaultValue: 'View SMS' });
    case 'questionnaire_sent':
      return t('patients.activity.action.view_questionnaire', {
        defaultValue: 'View questionnaire',
      });
    case 'punch_card_purchased':
    case 'punch_card_redeemed':
    case 'punch_card_returned':
      return t('patients.activity.action.view_punch_card', { defaultValue: 'View punch card' });
    default: {
      const _exhaustive: never = event.event_type;
      return _exhaustive;
    }
  }
}

export type ActivityNavigation =
  | { kind: 'appointment'; appointmentId: string }
  | { kind: 'path'; path: string };

export function activityNavigation(
  event: PatientActivityItem,
  patientId: string,
): ActivityNavigation | null {
  switch (event.event_type) {
    case 'appointment_booked':
    case 'appointment_rescheduled':
    case 'appointment_cancelled':
    case 'appointment_completed':
    case 'appointment_no_show':
    case 'appointment_reminder_sent':
      return event.appointment_id
        ? { kind: 'appointment', appointmentId: event.appointment_id }
        : event.event_type === 'appointment_reminder_sent'
          ? { kind: 'path', path: patientCommunicationPath(patientId, { channel: 'sms' }) }
          : null;
    case 'journal_created':
    case 'journal_finalized':
    case 'journal_amendment_finalized':
      if (!event.journal_entry_id) return null;
      return {
        kind: 'path',
        path: `/patients/${patientId}?tab=journal&entry=${event.journal_entry_id}`,
      };
    case 'invoice_created':
    case 'invoice_sent':
    case 'invoice_rykker':
    case 'invoice_paid':
    case 'invoice_voided':
    case 'payment_received':
    case 'payment_refunded':
      return event.invoice_id ? { kind: 'path', path: `/invoices/${event.invoice_id}` } : null;
    case 'email_sent':
      return { kind: 'path', path: patientCommunicationPath(patientId, { channel: 'email' }) };
    case 'sms_sent':
      return { kind: 'path', path: patientCommunicationPath(patientId, { channel: 'sms' }) };
    case 'questionnaire_sent':
      return { kind: 'path', path: `/patients/${patientId}?tab=questionnaires` };
    case 'punch_card_purchased':
    case 'punch_card_redeemed':
    case 'punch_card_returned':
      return { kind: 'path', path: `/patients/${patientId}?tab=punch_cards` };
    default: {
      const _exhaustive: never = event.event_type;
      return _exhaustive;
    }
  }
}

export function activityDayKey(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  const tz = 'Europe/Copenhagen';
  const day = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
  if (day === today) return 'today';
  return day;
}

export function formatActivityDayHeading(
  dayKey: string,
  t: ActivityTranslate,
  locale: string,
): string {
  if (dayKey === 'today') {
    return t('patients.activity.today', { defaultValue: 'Today' });
  }
  const date = new Date(`${dayKey}T12:00:00`);
  return new Intl.DateTimeFormat(locale === 'da' ? 'da-DK' : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

export function formatActivityTime(iso: string): string {
  return clockTime(iso) ?? '';
}

export function groupActivityByDay(
  events: readonly PatientActivityItem[],
  now: Date = new Date(),
): Array<{ dayKey: string; events: PatientActivityItem[] }> {
  const groups: Array<{ dayKey: string; events: PatientActivityItem[] }> = [];
  const index = new Map<string, PatientActivityItem[]>();
  for (const event of events) {
    const key = activityDayKey(event.occurred_at, now);
    const existing = index.get(key);
    if (existing) {
      existing.push(event);
    } else {
      const list = [event];
      index.set(key, list);
      groups.push({ dayKey: key, events: list });
    }
  }
  return groups;
}

export function activeActivityFilterKeys(state: ActivityFilterState): ActivityFilterChipKey[] {
  const keys: ActivityFilterChipKey[] = [];
  if (state.category !== 'all') keys.push('category');
  if (state.datePreset !== 'all') keys.push('date');
  return keys;
}

export function countActiveActivityFilters(state: ActivityFilterState): number {
  return activeActivityFilterKeys(state).length;
}

export type ActivityEmptyKind = 'search' | 'filtered' | 'none';

/** Which empty state the settled activity list should show. Search wins over filters. */
export function activityEmptyKind(search: string, filtersActive: boolean): ActivityEmptyKind {
  if (search.trim()) return 'search';
  if (filtersActive) return 'filtered';
  return 'none';
}

export function clearActivityFilter(
  state: ActivityFilterState,
  key: ActivityFilterChipKey,
): ActivityFilterState {
  switch (key) {
    case 'category':
      return { ...state, category: 'all' };
    case 'date':
      return { ...state, datePreset: 'all', customFrom: '', customTo: '' };
    default: {
      const _exhaustive: never = key;
      return _exhaustive;
    }
  }
}

export function compareActivityNewestFirst(a: PatientActivityItem, b: PatientActivityItem): number {
  const time = b.occurred_at.localeCompare(a.occurred_at);
  if (time !== 0) return time;
  return b.id.localeCompare(a.id);
}

export function activitySearchHaystack(event: PatientActivityItem, t: ActivityTranslate): string {
  return [
    activityTitle(event, t),
    activityDetail(event, t),
    event.payload.invoice_number,
    event.payload.service_name,
    event.payload.practitioner_name,
    event.payload.journal_title,
    event.payload.punch_card_name,
    event.payload.questionnaire_name,
    event.payload.email_subject,
    event.payload.payment_method,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function matchesActivitySearch(
  event: PatientActivityItem,
  query: string,
  t: ActivityTranslate,
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return activitySearchHaystack(event, t).includes(needle);
}
